import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useAuth, useRequestSession } from '@/lib/api/auth'
import { gqlRequest, graphqlErrorMessage } from '@/lib/api/client'
import { buffetStateSchema, buffetTone, remainingSeconds } from '@/lib/buffet'
import { createRecord, loadRecords, UpdateRecordMutation } from '@/lib/records'
import { useConfiguration } from '@/lib/useConfiguration'
import { Button } from './ui/button'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from './ui/dialog'
import { Input } from './ui/input'

export function BuffetDisplay({ restaurantId, screenId }: { restaurantId: string; screenId: string }) {
  const { session } = useAuth()
  const request = useRequestSession()
  const client = useQueryClient()
  const config = useConfiguration(request, restaurantId)
  const screen = config.config.buffetScreens.find((s) => s.id === screenId)
  const stateQuery = useQuery({
    queryKey: ['buffet', request.mode, restaurantId, screenId],
    enabled: Boolean(session),
    refetchInterval: 5000,
    queryFn: () => loadRecords(request, restaurantId, 'galley.buffetState'),
  })
  const row = stateQuery.data?.find((r) => buffetStateSchema.safeParse(r.document).data?.screenId === screenId)
  const state = buffetStateSchema.safeParse(row?.document).data
  const [now, setNow] = useState(Date.now())
  const [position, setPosition] = useState<string | null>(null)
  const [name, setName] = useState('')
  const [minutes, setMinutes] = useState('60')
  const [productId, setProductId] = useState<string | null>(null)
  const [amount, setAmount] = useState('1')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(timer)
  }, [])
  const pan = position ? state?.pans[position] : undefined
  const save = async (action: 'start' | 'finish' | 'waste') => {
    if (!state || !row || !position) {
      setError('Spara bufféskärmens konfiguration i backoffice först.')
      return
    }
    const duration = Number(minutes)
    if (action === 'start' && (!name.trim() || !Number.isFinite(duration) || duration < 1 || duration > 1440)) {
      setError('Ange rätt och en hålltid mellan 1 och 1440 minuter.')
      return
    }
    const wasteAmount = Number(amount.replace(',', '.'))
    if (action === 'waste' && (!Number.isFinite(wasteAmount) || wasteAmount <= 0)) {
      setError('Ange antal portioner som slängs.')
      return
    }
    setSaving(true)
    setError('')
    try {
      const pans = { ...state.pans }
      if (action === 'start') pans[position] = { name: name.trim(), productId, startedAt: new Date().toISOString(), durationMinutes: duration }
      else delete pans[position]
      await gqlRequest(request, UpdateRecordMutation, { restaurantId, id: row.id, updatedAt: row.updatedAt, document: { screenId, pans } })
      await createRecord(request, restaurantId, 'galley.buffetEvent', { screenId, position, action, pan: action === 'start' ? pans[position] : pan, completedAt: new Date().toISOString() })
      if (action === 'waste' && pan)
        await createRecord(request, restaurantId, `galley.waste.${new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Stockholm' }).format(new Date())}`, {
          productId: pan.productId,
          name: pan.name,
          amount: wasteAmount,
          unit: 'st',
          unitPriceOre: 0,
          category: 'expired',
          note: `Buffé: ${screen?.name}`,
          createdAt: new Date().toISOString(),
        })
      await client.invalidateQueries({ queryKey: ['buffet'] })
      setPosition(null)
    } catch (cause) {
      setError(`Kunde inte spara. Kontrollera uppdaterad ruta innan du försöker igen: ${graphqlErrorMessage(cause)}`)
      await client.invalidateQueries({ queryKey: ['buffet'] })
    } finally {
      setSaving(false)
    }
  }
  if (!session) return null
  if (config.isPending || stateQuery.isPending) return <p className="p-6">Laddar buffé…</p>
  if (config.error)
    return (
      <p role="alert" className="p-6">
        {graphqlErrorMessage(config.error)}
      </p>
    )
  if (config.data?.restaurant?.galleyEnabled !== true) return <p className="p-6">Bufféskärmen är inte aktiverad för restaurangen.</p>
  if (!screen) return <p className="p-6">Bufféskärmen finns inte i konfigurationen.</p>
  return (
    <main className="flex-1 space-y-4 overflow-auto p-4">
      <header className="flex items-center justify-between gap-4">
        <h1 className="text-2xl font-bold">{screen.name}</h1>
        <Button asChild variant="outline">
          <a href="/">Byt skärm</a>
        </Button>
      </header>
      {(stateQuery.error || error) && (
        <p role="alert" className="text-destructive">
          {error || graphqlErrorMessage(stateQuery.error)}
        </p>
      )}
      {!state && <p>Spara bufféskärmens konfiguration i backoffice för att aktivera rutorna.</p>}
      {screen.sections.map((section, sectionIndex) => (
        <section key={sectionIndex} className="space-y-3">
          <h2 className="text-xl font-bold">{section.name}</h2>
          <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${section.columns}, minmax(0, 1fr))` }}>
            {Array.from({ length: section.columns * section.rows }, (_, index) => {
              const key = `${sectionIndex}/${index}`
              const current = state?.pans[key]
              const seconds = current ? remainingSeconds(current.startedAt, current.durationMinutes, now) : 0
              const tone = current ? buffetTone(seconds, current.durationMinutes, screen.orangeThresholdPercent, screen.redThresholdPercent) : 'empty'
              return (
                <button
                  key={key}
                  type="button"
                  disabled={saving || !state || Boolean(stateQuery.error)}
                  className={`min-h-36 rounded-xl border-2 p-4 text-center buffet-${tone}`}
                  onClick={() => {
                    setPosition(key)
                    setName('')
                    setMinutes('60')
                    setProductId(null)
                    setError('')
                  }}
                >
                  <span className="block text-xl font-bold">{current?.name ?? 'Tom ruta'}</span>
                  {current && !screen.hideTimer && (
                    <span className="mt-2 block text-3xl font-bold tabular-nums">
                      {seconds < 0 ? '−' : ''}
                      {Math.floor(Math.abs(seconds) / 60)}:{String(Math.abs(seconds) % 60).padStart(2, '0')}
                    </span>
                  )}
                  {current && seconds <= 0 && <span className="mt-2 block font-bold">HÅLLTID SLUT</span>}
                </button>
              )
            })}
          </div>
        </section>
      ))}
      <Dialog
        open={position !== null}
        onOpenChange={(open) => {
          if (!open && !saving) setPosition(null)
        }}
      >
        <DialogContent>
          <DialogTitle>{pan ? pan.name : 'Starta hålltid'}</DialogTitle>
          <DialogDescription>{pan ? 'Avsluta när maten tagit slut eller registrera det som slängs.' : 'Välj rätt och hur länge maten får stå.'}</DialogDescription>
          {pan ? (
            <>
              <Button disabled={saving} onClick={() => save('finish')}>
                Maten slut · töm rutan
              </Button>
              <label>
                Antal portioner som slängs
                <Input value={amount} inputMode="decimal" onChange={(e) => setAmount(e.target.value)} />
              </label>
              <Button disabled={saving} variant="destructive" onClick={() => save('waste')}>
                Registrera svinn · töm rutan
              </Button>
            </>
          ) : (
            <>
              <div className="flex flex-wrap gap-2">
                {screen.presets.map((preset) => (
                  <Button
                    key={preset.name}
                    variant="outline"
                    onClick={() => {
                      setName(preset.name)
                      setMinutes(String(preset.durationMinutes))
                      setProductId(preset.productId)
                    }}
                  >
                    {preset.name} · {preset.durationMinutes} min
                  </Button>
                ))}
              </div>
              <label>
                Rätt
                <Input value={name} onChange={(e) => setName(e.target.value)} />
              </label>
              <label>
                Hålltid (minuter)
                <Input value={minutes} inputMode="numeric" onChange={(e) => setMinutes(e.target.value)} />
              </label>
              <Button disabled={saving || !name.trim()} onClick={() => save('start')}>
                Starta timer
              </Button>
            </>
          )}
          {error && (
            <p role="alert" className="text-destructive">
              {error}
            </p>
          )}
        </DialogContent>
      </Dialog>
    </main>
  )
}
